import { useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { REGISTRY } from "./objects/registry";
import { useSound } from "@/lib/SoundContext";

// Tom & Jerry squash & stretch — everything anchors at its base like real cartoon mass
const ANIMS = {
  wobble: { rotate: [0, -9, 8, -6, 4, 0], scale: [1, 1.1, 0.95, 1.05, 1], transition: { duration: 0.6 } },
  squish: { scaleX: [1, 1.28, 0.9, 1.12, 1], scaleY: [1, 0.72, 1.14, 0.94, 1], transition: { duration: 0.55 } },
  bounce: { y: [0, -22, 0, -8, 0], scaleY: [1, 1.08, 0.88, 1.04, 1], scaleX: [1, 0.94, 1.1, 0.98, 1], transition: { duration: 0.6 } },
  shake: { x: [0, -7, 7, -5, 3, 0], rotate: [0, -3, 3, -2, 1, 0], transition: { duration: 0.45 } },
  roll: { rotate: [0, -14, -6, 0], x: [0, 10, 3, 0], transition: { duration: 0.6 } },
};

// juicy gloss pass — one shared light source (the fridge bulb, upper left)
function Gloss() {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible" viewBox="0 0 100 100" aria-hidden="true">
      <ellipse cx="30" cy="22" rx="12" ry="5.5" fill="#FFF" opacity="0.28" transform="rotate(-24 30 22)" />
      <ellipse cx="26" cy="20" rx="4.5" ry="2" fill="#FFF" opacity="0.55" transform="rotate(-24 26 20)" />
      <circle cx="74" cy="26" r="2.2" fill="#FFF" opacity="0.7" />
      <circle cx="80" cy="33" r="1.4" fill="#FFF" opacity="0.55" />
    </svg>
  );
}

export default function FridgeObject({
  objectKey,
  size = 72,
  rot = 0,
  label,
  onClick,
  className = "",
  style = {},
  entrance = false,
  delay = 0,
}) {
  const { play } = useSound();
  const [tagPos, setTagPos] = useState(null);
  const def = REGISTRY[objectKey] || {};
  const Comp = def.Comp;
  const anim = ANIMS[def.anim] || ANIMS.wobble;
  const tag = label || def.name;

  return (
    <div
      data-testid={`food-item-${objectKey}`}
      className={`relative group select-none ${def.still ? "pointer-events-none" : "cursor-pointer"} ${tagPos ? "z-40" : ""} ${className}`}
      style={{ width: size, height: size, ...style }}
      onMouseEnter={(e) => setTagPos(e.currentTarget.getBoundingClientRect())}
      onMouseLeave={() => setTagPos(null)}
    >
      {/* contact shadow — stays put while the object squishes and hops above it */}
      <div
        className="absolute left-1/2 -translate-x-1/2 bottom-[-3%] w-[82%] h-[9%] rounded-[50%] pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(13,38,66,0.38) 0%, transparent 68%)", filter: "blur(2px)" }}
      />
      <motion.div
        className="w-full h-full"
        style={{ transformOrigin: "50% 100%" }}
        initial={entrance ? { y: -110, opacity: 0, rotate: rot - 10 } : false}
        animate={entrance ? { y: 0, opacity: 1, rotate: rot } : { rotate: rot }}
        whileHover={{ ...anim, transition: anim.transition }}
        whileTap={{ scaleX: 1.18, scaleY: 0.82 }}
        transition={entrance ? { type: "spring", stiffness: 260, damping: 11, delay } : { duration: 0.3 }}
        onHoverStart={() => !def.still && play(def.sound || "tick")}
        onClick={(e) => {
          if (def.still) return;
          play(def.sound || "tick");
          if (onClick) onClick(e);
        }}
      >
        {Comp ? <Comp /> : null}
        <Gloss />
      </motion.div>
      {/* label portal — escapes shelves, railings, overflow and stacking contexts entirely */}
      {tagPos && tag && !def.still &&
        createPortal(
          <div
            data-testid={`food-tag-${objectKey}`}
            className="food-tag !pointer-events-none"
            style={{
              position: "fixed",
              left: tagPos.left + tagPos.width / 2,
              top: tagPos.top - 10,
              transform: "translate(-50%, -100%)",
              zIndex: 100,
              opacity: 1,
            }}
          >
            {tag}
          </div>,
          document.body
        )}
    </div>
  );
}
